/**
 * Telemedicine · Phase 1 · Post-call confirmation (full-screen).
 *
 * Replaces the earlier PostCallCsatSheet bottom sheet — Figma node 6691:20555
 * specifies a full-screen layout, not a sheet, so this lives at its own
 * route and `app/video-call/[channel].tsx` does a `router.replace` into it
 * once the call ends with `wasConnectedRef === true`.
 *
 * Two outcomes:
 *   - "Avaliar"   → router.replace('/csat?source=postcall')
 *   - "Não agora" → router.replace('/diary') (home/primary screen is the diary)
 *
 * Header is the same `LobbyHeader` used by the lobby/permissions screens so
 * the chrome is visually identical across the in-call surfaces. Close (X) is
 * treated as "Não agora" — both dismissals route home, never back to the
 * dead in-call screen.
 */
import React, { useCallback } from 'react';
import { View, Text, StyleSheet, SafeAreaView, TouchableOpacity, Image } from 'react-native';
import { useRouter } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import * as Haptics from 'expo-haptics';
import LobbyHeader from '@/components/video/lobby/LobbyHeader';
import { useLanguage } from '@/contexts/LanguageContext';

const CREAM = '#F9F8F3';
const INK = '#151515';
const BLUE_HORIZON = '#669AB7';
const STAR_OUTLINE = '#A19F97';

export default function PostCallScreen() {
  const router = useRouter();
  const { t } = useLanguage();

  const goHome = useCallback(() => {
    router.replace('/diary');
  }, [router]);

  // Tapping a star jumps straight to the CSAT screen with the rating
  // pre-applied — saves a second redundant "Avaliar minha experiência" tap
  // and lets the user finish the survey in one gesture instead of two.
  // The rating is forwarded as a query param and consumed on mount in
  // app/csat.tsx so the topic chips for the matching bucket render
  // immediately.
  const handleStarPress = useCallback(
    (rating: number) => {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {});
      router.replace({
        pathname: '/csat',
        params: { source: 'postcall', rating: String(rating) },
      });
    },
    [router],
  );

  const handleSkip = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {});
    goHome();
  }, [goHome]);

  return (
    <SafeAreaView style={styles.safe}>
      <LobbyHeader title="Agendamento" onClose={goHome} />

      <View style={styles.body}>
        <Image
          source={require('@/assets/illustrations/postcall-meeting.png')}
          style={styles.illustration}
          resizeMode="contain"
        />

        <View style={styles.badgeRow}>
          <CheckBadge />
          <Text style={styles.badgeText}>{t('postCallCsat.sessionEnded')}</Text>
        </View>

        <Text style={styles.headline}>{t('postCallCsat.headline')}</Text>

        {/* 5-star picker — same Figma star path as the CSAT screen so the
            two surfaces feel like the same widget. Each tap routes to /csat
            with the rating already set. We render filled outlines only
            (never partially filled) since the picker has no "hover" state
            on touch. */}
        <View style={styles.starsRow}>
          {[1, 2, 3, 4, 5].map((n) => (
            <TouchableOpacity
              key={n}
              style={styles.starButton}
              onPress={() => handleStarPress(n)}
              activeOpacity={0.7}
              accessibilityRole="button"
              accessibilityLabel={`${n} ${n === 1 ? 'estrela' : 'estrelas'}`}
            >
              <Svg width={36} height={36} viewBox="0 0 20 20" fill="none">
                <Path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M10.9202 3.48068L12.4431 6.52297C12.5923 6.82163 12.8804 7.02873 13.2145 7.07666L16.6211 7.56702C17.4628 7.68854 17.7977 8.70863 17.1886 9.29312L14.7252 11.6602C14.4831 11.893 14.3729 12.2276 14.4301 12.5562L15.0115 15.898C15.1547 16.7247 14.2748 17.3554 13.5225 16.9643L10.4777 15.3854C10.1792 15.2305 9.82168 15.2305 9.52232 15.3854L6.4775 16.9643C5.72519 17.3554 4.84533 16.7247 4.98937 15.898L5.56987 12.5562C5.62714 12.2276 5.51694 11.893 5.27485 11.6602L2.8114 9.29312C2.20226 8.70863 2.5372 7.68854 3.37889 7.56702L6.78554 7.07666C7.11961 7.02873 7.40856 6.82163 7.55781 6.52297L9.07979 3.48068C9.45638 2.72845 10.5436 2.72845 10.9202 3.48068Z"
                  fill="transparent"
                  stroke={STAR_OUTLINE}
                  strokeWidth={1.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </Svg>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      <View style={styles.footer}>
        <TouchableOpacity
          style={styles.secondaryButton}
          onPress={handleSkip}
          activeOpacity={0.7}
          accessibilityRole="button"
        >
          <Text style={styles.secondaryButtonText}>{t('postCallCsat.skip')}</Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
}

/** Figma node 6691:20963 — 20×20 check badge in Blue Horizon. */
function CheckBadge() {
  return (
    <Svg width={20} height={20} viewBox="0 0 20 20" fill="none">
      <Path
        d="M10 1.667a8.333 8.333 0 1 0 0 16.666 8.333 8.333 0 0 0 0-16.666Z"
        fill={BLUE_HORIZON}
      />
      <Path
        d="m6.667 10 2.5 2.5L13.75 7.917"
        stroke="#FFFFFF"
        strokeWidth={1.667}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: CREAM },
  body: {
    flex: 1,
    paddingHorizontal: 40,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  illustration: { width: 260, height: 260 },
  badgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  badgeText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 19.6,
    color: BLUE_HORIZON,
  },
  headline: {
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 28.8,
    color: INK,
    textAlign: 'center',
  },
  footer: {
    padding: 24,
    gap: 8,
  },
  starsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 8,
  },
  starButton: {
    padding: 6,
  },
  secondaryButton: {
    height: 56,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  secondaryButtonText: {
    color: INK,
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
  },
});
